import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMonthCursor } from '@/hooks/useMonthCursor'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Pencil, Copy, Trash2, ChevronLeft, ChevronRight, Repeat, CheckCircle2, CircleDot, CreditCard, Wallet, Sparkles, AlertCircle } from 'lucide-react'
import { ConversionIndicator } from '@/components/ConversionIndicator'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { MonthPicker } from '@/components/ui/month-picker'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { StatementSwitcher } from '@/components/StatementSwitcher'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SortHead, useTableSort, cmp } from '@/components/ui/sortable-head'
import { useAccounts, useUpdateCardDays } from '@/hooks/useAccounts'
import {
  useTransactions,
  useDeleteTransaction,
  useToggleStatus,
  useSettleCardMonth,
  useSaveTransaction,
  type TransactionRow,
  type TransactionInput,
} from '@/hooks/useTransactions'
import { useHousehold } from '@/context/HouseholdContext'
import { useConfirm } from '@/context/ConfirmProvider'
import { useT } from '@/i18n/I18nProvider'
import { useCurrentRates, convertWithRates } from '@/hooks/useCurrentRates'
import { useCategories, buildTree, buildPicker } from '@/hooks/useCategories'
import { useTags } from '@/hooks/useTags'
import { FilterBar, EMPTY_FILTERS, type Filters } from '@/components/filters/FilterBar'
import { matchTx, categoryScope } from '@/components/filters/applyFilters'
import { Combobox, type ComboOption } from '@/components/ui/combobox'
import { amountInAccount, cardFaturaDebt, faturaEndISO, type FinanceTx } from '@/lib/finance'
import { Input } from '@/components/ui/input'
import { MoneyInput } from '@/components/ui/money-input'
import { DateInput } from '@/components/ui/date-input'
import { formatCurrency } from '@/lib/currency'
import { closeDateForStatement, dueDateForMonth, suggestStatementMonth } from '@/lib/cards'
import { TagBadges } from '@/components/transactions/TagBadges'
import { InstallmentBadge } from '@/components/transactions/InstallmentBadge'
import { TransactionFormDialog, defaultDateForMonth } from '@/components/transactions/TransactionFormDialog'
import { TransferDialog } from '@/components/transactions/TransferDialog'
import { ImportStatementDialog } from '@/components/transactions/ImportStatementDialog'
import { IncludeRecurringDialog } from '@/components/recurring/IncludeRecurringDialog'
import { useTranslateCategory } from '@/lib/categoryDisplay'

function monthLabel(d: Date) {
  return format(d, "MMMM 'de' yyyy", { locale: ptBR })
}
function isCurrentMonth(d: Date) {
  const n = new Date()
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth()
}
function monthBounds(d: Date) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1)
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { from: iso(first), to: iso(last) }
}

export function CardStatementPage() {
  const { t: tr } = useT()
  const tc = useTranslateCategory()
  const { id = '' } = useParams()
  const { baseCurrency } = useHousehold()
  const { data: accounts } = useAccounts(true)
  const del = useDeleteTransaction()
  const toggle = useToggleStatus()
  const settle = useSettleCardMonth()
  const confirm = useConfirm()

  const card = useMemo(() => (accounts ?? []).find((a) => a.id === id), [accounts, id])

  const [cursor, setCursor] = useMonthCursor()
  const range = useMemo(() => monthBounds(cursor), [cursor])
  // Todos os lançamentos do cartão (para calcular o saldo devedor acumulado até
  // o fim do mês — a "fatura" inclui o saldo de abertura, não só as compras do mês).
  const { data: allTxs, isLoading } = useTransactions({ accountId: id })
  const { data: rates } = useCurrentRates()
  const { data: categories } = useCategories()
  const { data: tags } = useTags()

  // FILTROS da fatura. Sem filtro de conta — aqui é um cartão só.
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const categoryOptions: ComboOption[] = useMemo(
    () =>
      (['expense', 'income', 'investment'] as const).flatMap((k) =>
        buildTree(categories ?? [], k).flatMap(({ parent, children }) => [
          { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
          ...children.map((c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name })),
        ]),
      ),
    [categories],
  )
  const tagOptions = useMemo(() => (tags ?? []).map((t) => t.name), [tags])
  // Categoria PAI traz os FILHOS junto.
  const catScope = useMemo(() => categoryScope(filters.categoryId, categories), [filters.categoryId, categories])

  const [dialogOpen, setDialogOpen] = useState(false)
  const [payOpen, setPayOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [includeOpen, setIncludeOpen] = useState(false)
  const [editing, setEditing] = useState<TransactionRow | null>(null)
  const [duplicating, setDuplicating] = useState<TransactionRow | null>(null)

  // Dias do cartão: FECHAMENTO (define em qual fatura a compra entra) e
  // VENCIMENTO (quando essa fatura é paga).
  const updateDays = useUpdateCardDays()
  const [dueInput, setDueInput] = useState('')
  const [closeInput, setCloseInput] = useState('')
  useEffect(() => {
    setDueInput(card?.due_day ? String(card.due_day) : '')
    setCloseInput(card?.closing_day ? String(card.closing_day) : '')
  }, [card?.due_day, card?.closing_day])
  function saveDay(field: 'due_day' | 'closing_day', raw: string) {
    const n = parseInt(raw, 10)
    const val = n >= 1 && n <= 31 ? n : null
    if (!card || val === (card[field] ?? null)) return
    const isDue = field === 'due_day'
    updateDays.mutate(
      { id, patch: { [field]: val } },
      {
        onSuccess: () => toast.success(isDue ? tr('acct.dueSaved') : tr('acct.closingSaved')),
        onError: (e) =>
          toast.error(isDue ? tr('acct.dueSaveError') : tr('acct.closingSaveError'), { description: (e as Error).message }),
      },
    )
  }
  // Datas DESTA fatura. A fatura é nomeada pelo mês do VENCIMENTO, então o
  // vencimento é o do próprio mês; o FECHAMENTO é que pode ser no mês anterior
  // (BAC: fatura de julho fecha 24/06 e vence 15/07).
  const closeThisMonth = closeDateForStatement(
    card?.due_day,
    card?.closing_day,
    cursor.getFullYear(),
    cursor.getMonth(),
  )
  const dueThisMonth = dueDateForMonth(card?.due_day, cursor.getFullYear(), cursor.getMonth())

  // Régua de estado da fatura pelo FECHAMENTO. `openMonth` = fatura em que uma compra
  // HOJE cairia (a que ainda está acumulando). O mês exibido é:
  //   == openMonth → ABERTA (acumulando);  < openMonth → já FECHOU;  > openMonth → futura.
  const p2 = (x: number) => String(x).padStart(2, '0')
  const cursorKey = `${cursor.getFullYear()}-${p2(cursor.getMonth() + 1)}`
  const openFaturaMonth = useMemo(() => {
    const d = new Date()
    const todayISO = `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`
    return suggestStatementMonth(todayISO, card?.closing_day, card?.due_day).slice(0, 7)
  }, [card?.closing_day, card?.due_day])
  const isFaturaAberta = cursorKey === openFaturaMonth

  function shiftMonth(delta: number) {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1))
  }

  // Edição INLINE na lista (igual à tela de Transações). O lápis segue abrindo o form.
  const save = useSaveTransaction()
  const [inlineEdit, setInlineEdit] = useState<{ id: string; field: 'description' | 'amount' | 'date' | 'category' } | null>(null)
  const [draft, setDraft] = useState('')
  function toInput(t: TransactionRow): TransactionInput {
    return {
      account_id: t.account_id,
      category_id: t.category_id,
      kind: t.kind,
      amount: Number(t.amount),
      currency: t.currency,
      date: t.date,
      description: t.description,
      status: t.status,
      exchange_rate: t.exchange_rate,
    }
  }
  function startEdit(t: TransactionRow, field: 'description' | 'amount' | 'date' | 'category') {
    setInlineEdit({ id: t.id, field })
    setDraft(field === 'amount' ? String(t.amount) : field === 'date' ? t.date : (t.description ?? ''))
  }
  async function saveInline(t: TransactionRow, patch: Partial<TransactionInput>) {
    setInlineEdit(null)
    try {
      await save.mutateAsync({ id: t.id, values: { ...toInput(t), ...patch } })
    } catch (e) {
      toast.error(tr('acct.saveError'), { description: (e as Error).message })
    }
  }
  function commitText(t: TransactionRow) {
    if (!inlineEdit) return
    if (inlineEdit.field === 'description') void saveInline(t, { description: draft.trim() || null })
    else if (inlineEdit.field === 'amount') {
      const n = Number(draft)
      if (draft.trim() !== '' && Number.isFinite(n) && n >= 0) void saveInline(t, { amount: n })
      else setInlineEdit(null)
    } else if (inlineEdit.field === 'date') {
      if (draft) void saveInline(t, { date: draft })
      else setInlineEdit(null)
    }
  }
  const isEditing = (id: string, field: string) => inlineEdit?.id === id && inlineEdit.field === field

  function openNew() {
    setEditing(null)
    setDuplicating(null)
    setDialogOpen(true)
  }
  function openEdit(t: TransactionRow) {
    setEditing(t)
    setDuplicating(null)
    setDialogOpen(true)
  }
  function openDuplicate(t: TransactionRow) {
    setEditing(null)
    setDuplicating(t)
    setDialogOpen(true)
  }
  async function remove(t: TransactionRow) {
    const isTransfer = Boolean(t.transfer_group_id)
    const ok = await confirm({
      title: isTransfer ? tr('acct.deleteTransferTitle') : tr('acct.deleteTxTitle'),
      description: isTransfer ? tr('acct.deleteTransferDesc') : tr('acct.cannotUndo'),
      confirmText: tr('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync({ id: t.id, transferGroupId: t.transfer_group_id })
      toast.success(isTransfer ? tr('acct.transferDeleted') : tr('acct.txDeleted'))
    } catch (e) {
      toast.error(tr('acct.deleteTxError'), { description: (e as Error).message })
    }
  }
  async function onToggle(t: TransactionRow, checked: boolean) {
    try {
      await toggle.mutateAsync({ id: t.id, status: checked ? 'paid' : 'pending' })
      toast.success(checked ? tr('acct.markedPaid') : tr('acct.markedPending'))
    } catch (e) {
      toast.error(tr('acct.statusError'), { description: (e as Error).message })
    }
  }

  // Esta tela raciocina pela régua da FATURA (byStatement): um lançamento pertence
  // ao mês da fatura em que é COBRADO — que é o mês da data, a menos que a compra
  // tenha sido feita depois do fechamento e empurrada para a fatura seguinte.
  //
  // Não confundir com a DÍVIDA (régua por data), que é o que o dashboard mostra:
  // a compra empurrada já é dívida hoje, mesmo sem estar nesta fatura. `naProxima`
  // abaixo é exatamente essa diferença, e a tela a exibe em vez de escondê-la.
  const { txs, faturaDebt, comprasPorMoeda, pagamentosMes } = useMemo(() => {
    const cardCcy = card?.currency ?? baseCurrency
    const all = (allTxs ?? []) as (TransactionRow & FinanceTx)[]
    let pagamentos = 0
    let empurrado = 0
    // Compras da fatura NA MOEDA ORIGINAL de cada lançamento. Cartão de dupla moeda
    // (BAC: GTQ + USD) tem uma fatura por moeda e o banco NUNCA converte — o valor
    // que se paga é "Q 5.541,00 + US$ 75,36", não a soma dos dois numa moeda só.
    const compras = new Map<string, number>()
    const monthTxs: TransactionRow[] = []
    for (const t of all) {
      const fim = faturaEndISO(t) // fim do mês da fatura que cobra este lançamento
      if (fim > range.to) {
        // já é dívida (a data é <= hoje), mas só será cobrada numa fatura futura
        if (t.date <= range.to && !t.transfer_group_id) {
          empurrado += (t.kind === 'expense' ? 1 : -1) * amountInAccount(t, cardCcy, rates)
        }
        continue
      }
      if (fim < range.from) continue
      monthTxs.push(t)
      if (t.transfer_group_id) {
        if (t.kind === 'income') pagamentos += amountInAccount(t, cardCcy, rates)
      } else {
        const sign = t.kind === 'expense' ? 1 : -1 // estorno abate
        compras.set(t.currency, (compras.get(t.currency) ?? 0) + sign * Number(t.amount))
      }
    }
    monthTxs.sort((a, b) => a.date.localeCompare(b.date) || a.created_at.localeCompare(b.created_at))
    const debt = card
      ? cardFaturaDebt({ initial_balance: card.initial_balance, currency: cardCcy }, all, range.to, rates, {
          byStatement: true,
        })
      : 0
    const base = convertWithRates(debt, cardCcy, rates)
    return {
      txs: monthTxs,
      faturaDebt: debt,
      faturaBase: base,
      comprasPorMoeda: [...compras].filter(([, v]) => Math.abs(v) > 0.005),
      pagamentosMes: pagamentos,
      naProxima: empurrado,
    }
  }, [allTxs, card, range, rates, baseCurrency])

  const cardCurrency = card?.currency ?? baseCurrency
  // No extrato do cartão, a ENTRADA de uma transferência é um PAGAMENTO da fatura.
  // A descrição padrão da transferência é "Transferência" — rotula como "Pagamento"
  // (mantém a descrição se o usuário tiver escrito algo específico).
  const descLabel = (t: { description: string | null; transfer_group_id: string | null; kind: string }) =>
    t.transfer_group_id && t.kind === 'income' && (!t.description || t.description === 'Transferência')
      ? tr('acct.paymentLabel')
      : t.description
  // "Paga" = não sobrou saldo devedor E houve movimento na fatura (compra ou pagamento).
  const isPaga = faturaDebt < 0.005 && (comprasPorMoeda.length > 0 || pagamentosMes > 0.005)
  // Fatura FECHADA e NÃO PAGA: já passou do fechamento (mês < o da fatura em aberto)
  // e ainda sobra saldo devedor.
  const isFaturaFechadaNaoPaga = cursorKey < openFaturaMonth && faturaDebt > 0.005

  // Ordenação da lista (padrão: por data). Não afeta o cálculo da fatura.
  type SKey = 'date' | 'desc' | 'cat' | 'value'
  const { sort, toggle: toggleSort } = useTableSort<SKey>('date', 'desc', ['value'])
  const rows = useMemo(() => {
    const val = (t: TransactionRow): string | number => {
      switch (sort.key) {
        case 'date':
          return t.date + t.created_at
        case 'desc':
          return (t.description ?? '').toLowerCase()
        case 'cat':
          return (t.category?.name ?? '').toLowerCase()
        case 'value':
          return (t.kind === 'expense' ? -1 : 1) * Number(t.amount)
      }
    }
    // Filtra só a EXIBIÇÃO — o cálculo da fatura acima continua sobre tudo.
    return txs.filter((t) => matchTx(t, filters, catScope)).sort((a, b) => cmp(val(a), val(b), sort.dir))
  }, [txs, sort, filters, catScope])

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <PageHeader
          title={
            <StatementSwitcher
              current={card ?? undefined}
              options={(accounts ?? []).filter((a) => a.type === 'cartao' && (!a.archived || a.id === id))}
              basePath="/cartoes"
            />
          }
          actions={
            <>
              <Button variant="outline" onClick={() => setIncludeOpen(true)}>
                <Repeat className="size-4" /> {tr('acct.includeRecurring')}
              </Button>
              <Button variant="outline" onClick={() => setPayOpen(true)} disabled={faturaDebt <= 0}>
                <Wallet className="size-4" /> {tr('acct.payInvoice')}
              </Button>
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Sparkles className="size-4" /> {tr('acct.importInvoice')}
              </Button>
              <Button onClick={openNew}>
                <Plus className="size-4" /> {tr('acct.newTransaction')}
              </Button>
            </>
          }
        />
        {/* Dados do cartão (menores e um pouco separados do topo). */}
        <div className="text-muted-foreground border-border/60 mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2 text-xs">
          <span className="flex items-center gap-1.5">
            <label htmlFor="card-close-day" title={tr('acct.closingDayTitle')}>
              {tr('acct.closingDayLabel')}
            </label>
            <Input
              id="card-close-day"
              type="number"
              min={1}
              max={31}
              value={closeInput}
              placeholder="—"
              onChange={(e) => setCloseInput(e.target.value)}
              onBlur={() => saveDay('closing_day', closeInput)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              }}
              className="h-7 w-14"
            />
          </span>
          <span className="flex items-center gap-1.5">
            <label htmlFor="card-due-day">{tr('acct.dueDayLabel')}</label>
            <Input
              id="card-due-day"
              type="number"
              min={1}
              max={31}
              value={dueInput}
              placeholder="—"
              onChange={(e) => setDueInput(e.target.value)}
              onBlur={() => saveDay('due_day', dueInput)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              }}
              className="h-7 w-14"
            />
          </span>
          {(closeThisMonth || dueThisMonth) && (
            <span className="text-muted-foreground/80">
              {tr('acct.invoiceOf')} <span className="capitalize">{monthLabel(cursor)}</span>
              {closeThisMonth && <> · {tr('acct.closesOn', { date: closeThisMonth })}</>}
              {dueThisMonth && <> · <strong className="text-foreground">{tr('acct.dueOn', { date: dueThisMonth })}</strong></>}
            </span>
          )}
        </div>
      </div>

      {/* Navegador de mês + total da fatura */}
      <div className="flex items-center justify-between rounded-lg border p-2">
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} aria-label={tr('acct.prevMonth')}>
          <ChevronLeft className="size-5" />
        </Button>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <MonthPicker value={cursor} onChange={setCursor} />
            {isPaga && (
              <Badge variant="secondary" className="gap-1 text-success">
                <CheckCircle2 className="size-3" /> {tr('acct.invoicePaid')}
              </Badge>
            )}
            {isFaturaAberta && !isPaga && (
              <Badge variant="secondary" className="gap-1 text-amber-600">
                <CircleDot className="size-3" /> {tr('acct.invoiceOpen')}
              </Badge>
            )}
            {isFaturaFechadaNaoPaga && (
              <Badge variant="secondary" className="text-destructive gap-1">
                <AlertCircle className="size-3" /> {tr('acct.invoiceClosedUnpaid')}
              </Badge>
            )}
          </div>
          {/* Compras DA FATURA na moeda original. Num cartão de dupla moeda o banco
              cobra cada moeda separadamente — mostrar "Q x + US$ y" é o valor real
              a pagar; somar tudo numa moeda só seria um número que não existe. */}
          {comprasPorMoeda.length > 0 && (
            <div className="text-sm">
              {tr('acct.purchases')}{' '}
              {comprasPorMoeda.map(([ccy, v], i) => (
                <span key={ccy}>
                  {i > 0 && <span className="text-muted-foreground"> + </span>}
                  <span className="tabular-nums font-medium">{formatCurrency(v, ccy)}</span>
                </span>
              ))}
            </div>
          )}
          <div className="text-muted-foreground text-xs">
            {tr('acct.debtBalance')} <span className="tabular-nums">{formatCurrency(faturaDebt, cardCurrency)}</span>
            {pagamentosMes > 0.005 && (
              <> · {tr('acct.paidLower')} <span className="tabular-nums">{formatCurrency(pagamentosMes, cardCurrency)}</span></>
            )}
          </div>
          {!isCurrentMonth(cursor) && (
            <button className="text-primary text-xs hover:underline" onClick={() => setCursor(new Date())}>
              {tr('acct.backToCurrentMonth')}
            </button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} aria-label={tr('acct.nextMonth')}>
          <ChevronRight className="size-5" />
        </Button>
      </div>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        categories={categoryOptions}
        tags={tagOptions}
        placeholder={tr('acct.filterPlaceholder')}
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : rows.length > 0 ? (
            <>
            <div className="hidden overflow-x-auto md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" title={tr('acct.paidQ')}>✓</TableHead>
                    <SortHead label={tr('acct.colDate')} k="date" sort={sort} onToggle={toggleSort} />
                    <SortHead label={tr('acct.colDesc')} k="desc" sort={sort} onToggle={toggleSort} />
                    <SortHead label={tr('acct.colCategory')} k="cat" sort={sort} onToggle={toggleSort} />
                    <SortHead label={tr('acct.colValue')} k="value" sort={sort} onToggle={toggleSort} className="text-right" />
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((t) => (
                    <TableRow key={t.id} className={t.status === 'pending' ? 'opacity-70' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={t.status === 'paid'}
                          onCheckedChange={(v) => onToggle(t, v === true)}
                          aria-label={t.status === 'paid' ? tr('acct.markPending') : tr('acct.markPaid')}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {isEditing(t.id, 'date') ? (
                          <DateInput
                            value={draft}
                            onChange={(iso) => iso && void saveInline(t, { date: iso })}
                            onBlur={() => setInlineEdit(null)}
                            onKeyDown={(e) => e.key === 'Escape' && setInlineEdit(null)}
                          />
                        ) : (
                          <span className="cursor-pointer hover:underline" onClick={() => startEdit(t, 'date')}>
                            {format(new Date(t.date + 'T00:00:00'), 'dd/MM/yyyy')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing(t.id, 'description') ? (
                          <Input
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => commitText(t)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitText(t)
                              if (e.key === 'Escape') setInlineEdit(null)
                            }}
                            className="h-8"
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {t.recurring_id && <Repeat className="text-muted-foreground size-3 shrink-0" />}
                            <span
                              className="max-w-[200px] cursor-pointer truncate hover:underline"
                              onClick={() => startEdit(t, 'description')}
                            >
                              {descLabel(t) || <span className="text-muted-foreground">—</span>}
                            </span>
                            <span className="flex shrink-0 items-center gap-1">
                              {t.status === 'pending' && <Badge variant="outline">{tr('acct.pending')}</Badge>}
                              <InstallmentBadge n={t.installment_no} total={t.installment_total} description={t.description} />
                              <TagBadges tags={t.tags} />
                            </span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditing(t.id, 'category') ? (
                          <Combobox
                            autoOpen
                            className="h-8"
                            value={t.category_id ?? 'none'}
                            onChange={(v) => void saveInline(t, { category_id: v === 'none' ? null : v })}
                            onClose={() => setInlineEdit(null)}
                            searchPlaceholder={tr('dlg.search')}
                            aria-label={tr('acct.colCategory')}
                            options={[
                              { value: 'none', label: '—' },
                              ...buildPicker(categories ?? [], t.kind === 'income' ? 'income' : 'expense').flatMap(
                                ({ parent, children }) => [
                                  { value: parent.id, label: tc(parent.name), bold: true } as ComboOption,
                                  ...children.map(
                                    (c): ComboOption => ({ value: c.id, label: tc(c.name), depth: 1, keywords: parent.name }),
                                  ),
                                ],
                              ),
                            ]}
                          />
                        ) : (
                          <span className="cursor-pointer hover:underline" onClick={() => startEdit(t, 'category')}>
                            {tc(t.category?.name) || '—'}
                          </span>
                        )}
                      </TableCell>
                      <TableCell
                        className={`text-right tabular-nums whitespace-nowrap ${t.kind === 'income' ? 'text-success' : ''}`}
                      >
                        {isEditing(t.id, 'amount') ? (
                          <MoneyInput
                            autoFocus
                            value={draft ? Number(draft) : 0}
                            onChange={(n) => setDraft(n ? String(n) : '')}
                            onBlur={() => commitText(t)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') commitText(t)
                              if (e.key === 'Escape') setInlineEdit(null)
                            }}
                            className="h-8 w-28 text-right"
                          />
                        ) : (
                          <span
                            className="inline-flex cursor-pointer items-center justify-end gap-1 hover:underline"
                            onClick={() => startEdit(t, 'amount')}
                          >
                            <span>
                              {t.kind === 'expense' ? '-' : '+'}
                              {formatCurrency(Number(t.amount), t.currency)}
                            </span>
                            {t.currency !== baseCurrency &&
                              (t.amount_base != null ? (
                                <ConversionIndicator
                                  amount={Number(t.amount)}
                                  currency={t.currency}
                                  baseCurrency={baseCurrency}
                                  converted={Number(t.amount_base)}
                                  rate={t.exchange_rate}
                                />
                              ) : (
                                <AlertCircle className="text-muted-foreground size-3" aria-label={tr('acct.conversionPending')} />
                              ))}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(t)} aria-label={tr('common.edit')}>
                            <Pencil className="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openDuplicate(t)} aria-label={tr('acct.duplicate')}>
                            <Copy className="size-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(t)} aria-label={tr('common.delete')}>
                            <Trash2 className="text-destructive size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-2 p-3 md:hidden">
              {rows.map((t) => (
                <div key={t.id} className={`rounded-xl border p-3 ${t.status === 'pending' ? 'opacity-70' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {t.recurring_id && <Repeat className="text-muted-foreground size-3 shrink-0" />}
                      <span className="truncate font-medium">
                        {descLabel(t) || <span className="text-muted-foreground">—</span>}
                      </span>
                      <span className="flex shrink-0 items-center gap-1">
                        {t.status === 'pending' && <Badge variant="outline">{tr('acct.pending')}</Badge>}
                        <InstallmentBadge n={t.installment_no} total={t.installment_total} description={t.description} />
                        <TagBadges tags={t.tags} />
                      </span>
                    </div>
                    <span
                      className={`inline-flex shrink-0 items-center gap-1 text-right font-bold tabular-nums whitespace-nowrap ${t.kind === 'income' ? 'text-success' : ''}`}
                    >
                      <span>
                        {t.kind === 'expense' ? '-' : '+'}
                        {formatCurrency(Number(t.amount), t.currency)}
                      </span>
                      {t.currency !== baseCurrency &&
                        (t.amount_base != null ? (
                          <ConversionIndicator
                            amount={Number(t.amount)}
                            currency={t.currency}
                            baseCurrency={baseCurrency}
                            converted={Number(t.amount_base)}
                            rate={t.exchange_rate}
                          />
                        ) : (
                          <AlertCircle className="text-muted-foreground size-3" aria-label={tr('acct.conversionPending')} />
                        ))}
                    </span>
                  </div>
                  <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    <span className="tabular-nums">{format(new Date(t.date + 'T00:00:00'), 'dd/MM/yyyy')}</span>
                    <span>{tc(t.category?.name) || '—'}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={t.status === 'paid'}
                        onCheckedChange={(v) => onToggle(t, v === true)}
                        aria-label={t.status === 'paid' ? tr('acct.markPending') : tr('acct.markPaid')}
                      />
                      <span className="text-muted-foreground text-xs">{t.status === 'paid' ? tr('acct.paid') : tr('acct.pending')}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} aria-label={tr('common.edit')}>
                        <Pencil className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openDuplicate(t)} aria-label={tr('acct.duplicate')}>
                        <Copy className="size-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(t)} aria-label={tr('common.delete')}>
                        <Trash2 className="text-destructive size-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          ) : txs.length > 0 ? (
            // Há lançamentos na fatura (os totais acima os refletem), mas o
            // filtro/busca não casou nenhum. Mensagem neutra, sem o CTA de
            // primeira vez — que só faz sentido quando a fatura está de fato vazia.
            <div className="text-muted-foreground p-8 text-center text-sm">
              {tr('acct.noFilterMatch')}
            </div>
          ) : (
            <EmptyState
              icon={CreditCard}
              title={tr('acct.emptyMonth', { month: monthLabel(cursor) })}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {tr('acct.newTransaction')}
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <TransactionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        transaction={editing}
        duplicateFrom={duplicating}
        defaultAccountId={id}
        defaultDate={defaultDateForMonth(cursor)}
      />
      <TransferDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        defaultToId={id}
        defaultAmount={faturaDebt.toFixed(2)}
        defaultDate={range.to}
        payMode
        title={tr('acct.payInvoiceTitle', { name: card?.name ?? tr('acct.cardFallback') })}
        onDone={async () => {
          // A fatura foi paga -> as compras COBRADAS nela estão liquidadas.
          const n = await settle.mutateAsync({
            cardId: id,
            year: cursor.getFullYear(),
            month0: cursor.getMonth(),
          })
          if (n > 0) toast.success(tr('acct.invoiceSettled', { n }))
          else toast.success(tr('acct.paymentRegistered'))
        }}
      />
      <IncludeRecurringDialog
        open={includeOpen}
        onOpenChange={setIncludeOpen}
        year={cursor.getFullYear()}
        month0={cursor.getMonth()}
        accountId={id}
        title={tr('acct.includeRecurringTitle', { name: card?.name ?? tr('acct.cardFallback') })}
      />
      <ImportStatementDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        cardId={id}
        cardCurrency={card?.currency ?? baseCurrency}
        statementMonth={`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`}
      />
    </div>
  )
}
